import React from 'react';

import type { OnOpenChangeArgs } from '@atlaskit/dropdown-menu';
import { FlyoutMenuItemTrigger, MenuList } from '@atlaskit/navigation-system';
import { componentWithFG } from '@atlaskit/platform-feature-flags-react';
import {
	type ItemDragAndDropGroup,
	NavItemActionsMenu,
	NavItemReorderingMenu,
	NavItemVisibilityToggle,
} from '@atlassian/navigation-drag-and-drop';

import { useItemProperties } from '../../../controllers/items-and-shortcuts-provider/use-item-properties';

import { useNavItemReorderingContext } from './use-nav-item-reordering-context';
import { useNavItemVisibilityActions } from './use-nav-item-visibility-actions';
import { useReorderingActions } from './use-reordering-actions';

export type NavItemActionsProps = {
	group?: ItemDragAndDropGroup;
	itemMenuId: string;
	/**
	 * Callback invoked when the actions menu or the reordering menu is opened or closed.
	 * If this menu is nested inside another menu, this can be used to close that one when this one closes.
	 */
	onOpenChange?: (args: OnOpenChangeArgs) => void;
};

function NavItemActionsOld({
	itemMenuId,
	onOpenChange,
	renderFromLevel = 0,
	group,
}: NavItemActionsProps & {
	/**
	 * Level from which to start rendering the full menu.
	 * - `0`: full actions menu with its own trigger
	 * - `1`: only reordering and visibility toggle without surrounding menu
	 */
	renderFromLevel?: 0 | 1;
}) {
	const { moveUpPosition, moveDownPosition } = useNavItemReorderingContext(itemMenuId, group);
	const { moveToTop, moveToBottom, moveUp, moveDown } = useReorderingActions(itemMenuId, group);
	const { getItemProperties } = useItemProperties();
	const { isReorderable, isHideable, isVisible } = getItemProperties(itemMenuId) || {};
	const { hideItem, showItem } = useNavItemVisibilityActions(itemMenuId);

	if (!isReorderable && !isHideable) {
		return null;
	}

	return (
		<div data-testid="gsn-nav-item-actions">
			{renderFromLevel === 0 ? (
				<NavItemActionsMenu
					isReorderable={isReorderable}
					isVisible={isVisible}
					canToggleVisibility={isHideable}
					moveUp={moveUp}
					moveDown={moveDown}
					moveToTop={moveToTop}
					moveToBottom={moveToBottom}
					canMoveUp={moveUpPosition !== false}
					canMoveDown={moveDownPosition !== false}
					toggleVisibility={isVisible ? hideItem : showItem}
					onOpenChange={onOpenChange}
				/>
			) : (
				<MenuList>
					{isReorderable && (
						<NavItemReorderingMenu
							TriggerComponent={FlyoutMenuItemTrigger}
							moveUp={moveUp}
							moveDown={moveDown}
							moveToTop={moveToTop}
							moveToBottom={moveToBottom}
							canMoveUp={moveUpPosition !== false}
							canMoveDown={moveDownPosition !== false}
							onOpenChange={onOpenChange}
						/>
					)}
					{isHideable && (
						<NavItemVisibilityToggle
							isVisible={isVisible}
							toggleVisibility={isVisible ? hideItem : showItem}
						/>
					)}
				</MenuList>
			)}
		</div>
	);
}

function NavItemActionsNew({ itemMenuId, onOpenChange, group }: NavItemActionsProps) {
	const { moveUpPosition, moveDownPosition } = useNavItemReorderingContext(itemMenuId, group);
	const { moveToTop, moveToBottom, moveUp, moveDown } = useReorderingActions(itemMenuId, group);
	const { getItemProperties } = useItemProperties();
	const { isReorderable, isHideable, isVisible } = getItemProperties(itemMenuId) || {};
	const { hideItem, showItem } = useNavItemVisibilityActions(itemMenuId);

	if (!isReorderable && !isHideable) {
		return null;
	}

	return (
		<div data-testid="gsn-nav-item-actions">
			<NavItemActionsMenu
				isReorderable={isReorderable}
				isVisible={isVisible}
				canToggleVisibility={isHideable}
				moveUp={moveUp}
				moveDown={moveDown}
				moveToTop={moveToTop}
				moveToBottom={moveToBottom}
				canMoveUp={moveUpPosition !== false}
				canMoveDown={moveDownPosition !== false}
				toggleVisibility={isVisible ? hideItem : showItem}
				onOpenChange={onOpenChange}
			/>
		</div>
	);
}

export default componentWithFG(
	'navx-1416-nav-item-actions-menu-refactor',
	NavItemActionsNew,
	NavItemActionsOld,
);
